import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, type WhatsappSignupTemplateRow } from "../lib/api";
import { generateRequestIdV7 } from "../lib/dispatch";
import { useCachedResource } from "../lib/resource-cache";
import {
  describeActivateFailure,
  describeTemplateCheckFailure,
  parseEmbeddedSignupMessage,
  templateCategoryNote,
  templateRowState,
} from "../lib/wa-signup";

/**
 * /whatsapp-signup — WhatsApp Embedded Signup (coexistence onboarding).
 *
 * 2026-08-25 (whatsapp-embedded-signup-page). Hosts Meta's Embedded
 * Signup popup so the studio's WhatsApp Business App number can be
 * coexistence-onboarded onto the Cloud API (runbook
 * `build-output/runbooks/whatsapp-coexistence-migration.md` §3-4). Flow:
 *
 *   1. GET /api/whatsapp-signup/:slug → the non-secret popup constants
 *      (Meta app id + Facebook Login for Business config id) + the
 *      pending-signup presence flags.
 *   2. Load Meta's JS SDK (connect.facebook.net — the SPA's one external
 *      script) and FB.init with those constants.
 *   3. Launch → FB.login opens the popup with
 *      `featureType: "whatsapp_business_app_onboarding"`, the flag Meta's
 *      coexistence page names for offering "connect your existing WhatsApp
 *      Business app number". The launch names no version, and Meta serves
 *      such a launch as v4, whose extras object is "purposely empty", so
 *      the flag is probably ignored: on 2026-10-27 a Business-app number
 *      was offered once its account sat in a portfolio the popup lists
 *      (switch runbook §2a). It stays, since leaving it out is untested.
 *      The operator signs into the BUSINESS's Facebook and the phone
 *      confirms in-app.
 *   4. Two async results race back: the popup's window message (waba_id,
 *      maybe phone_number_id) and the FB.login callback (the single-use
 *      token code). When BOTH are in hand the page auto-POSTs the
 *      completion — the code is short-lived, so no manual submit step.
 *   5. The backend exchanges + verifies, then PARKS the credentials
 *      (token, phone, WABA id) as pending secret kinds. Production keeps
 *      sending from the current number and the app is NOT yet subscribed
 *      to the new WABA, so the new number's messages do not reach the AI
 *      either — the explicit Activate button below subscribes AND repoints
 *      in one step (runbook §6). If the WABA lists several numbers the
 *      backend answers with a phone chooser instead (no new popup run
 *      needed — the parked token covers the follow-up).
 *   6. While a complete signup is parked, the Pending activation panel
 *      checks every template the studio can send against the new WABA
 *      (op `check_templates`, wa-activate-template-check 2026-10-28) and
 *      keeps Activate locked until all of them are APPROVED. The server
 *      refuses an Activate press on the same check, so the lock is a
 *      courtesy, not the gate.
 */

interface FbSdk {
  init(opts: {
    appId: string;
    autoLogAppEvents: boolean;
    xfbml: boolean;
    version: string;
  }): void;
  login(
    cb: (response: {
      authResponse?: { code?: string } | null;
      status?: string;
    }) => void,
    opts: {
      config_id: string;
      response_type: string;
      override_default_response_type: boolean;
      extras: Record<string, unknown>;
    },
  ): void;
}

declare global {
  interface Window {
    FB?: FbSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_SCRIPT_ID = "facebook-jssdk";
const SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";

interface SignupConstants {
  readonly appId: string;
  readonly configId: string;
  readonly graphVersion: string;
}

interface PendingPresence {
  readonly access_token: boolean;
  readonly phone_number_id: boolean;
  readonly waba_id: boolean;
}

interface PendingResult {
  readonly waba_id: string;
  readonly phone_number_id: string;
  readonly display_phone_number: string | null;
}

interface PhoneChoice {
  readonly wabaId: string;
  readonly rows: ReadonlyArray<{
    readonly id: string;
    readonly display_phone_number: string | null;
  }>;
}

export function WhatsappSignup(): JSX.Element {
  const { data: who } = useCachedResource("whoami", () => api.whoami());
  const slug = who?.studio?.slug ?? null;

  const [constants, setConstants] = useState<SignupConstants | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [pending, setPending] = useState<PendingPresence | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [session, setSession] = useState<{
    wabaId: string;
    phoneNumberId: string | null;
    event: string;
  } | null>(null);
  const [cancelStep, setCancelStep] = useState<string | null>(null);
  // A WA_EMBEDDED_SIGNUP message the parser could not classify. Kept so a
  // popup shape change is visible on the page instead of looking like a
  // popup that silently never finished (2026-10-11).
  const [unrecognised, setUnrecognised] = useState<{
    event: string;
    dataKeys: readonly string[];
  } | null>(null);
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  // The parked-pending result card persists until its own Dismiss — the
  // flow cannot be re-run to regenerate it (the code is single-use) — or
  // until an Activate press, after which its "not subscribed yet" may be
  // false; the panels that follow the server state take over.
  const [result, setResult] = useState<PendingResult | null>(null);
  const [phoneChoice, setPhoneChoice] = useState<PhoneChoice | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [activateArmed, setActivateArmed] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [activated, setActivated] = useState<{
    phone_number_id: string;
    waba_id: string;
    pendingCleared: boolean;
    warning: string | null;
  } | null>(null);
  const submittedRef = useRef(false);
  // The template check of the parked signup (wa-activate-template-check).
  const [templateCheck, setTemplateCheck] = useState<{
    ready: boolean;
    rows: ReadonlyArray<WhatsappSignupTemplateRow>;
    liveRead: boolean;
  } | null>(null);
  const [checkingTemplates, setCheckingTemplates] = useState(false);
  const [templateCheckError, setTemplateCheckError] = useState<string | null>(
    null,
  );
  const [templateNonce, setTemplateNonce] = useState(0);
  const recheckTemplates = useCallback(
    () => setTemplateNonce((n) => n + 1),
    [],
  );

  // 1 — the popup constants + the pending-signup presence flags.
  // refresh() re-reads after each op so the Activate panel tracks state.
  const [configNonce, setConfigNonce] = useState(0);
  const refreshConfig = useCallback(() => setConfigNonce((n) => n + 1), []);
  useEffect(() => {
    if (slug === null) return;
    let cancelled = false;
    api
      .getWhatsappSignupConfig(slug)
      .then((r) => {
        if (cancelled) return;
        setConfigured(r.configured);
        setPending(r.pending ?? null);
        if (
          r.configured &&
          r.app_id !== undefined &&
          r.config_id !== undefined &&
          r.graph_version !== undefined
        ) {
          setConstants({
            appId: r.app_id,
            configId: r.config_id,
            graphVersion: r.graph_version,
          });
        }
        setLoadError(null);
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, configNonce]);

  // 1b — the template check, whenever a complete signup is parked: every
  // template the studio can send against the new WABA. Read only.
  const parkedComplete =
    pending !== null &&
    pending.access_token &&
    pending.phone_number_id &&
    pending.waba_id;
  useEffect(() => {
    if (slug === null || !parkedComplete) {
      setTemplateCheck(null);
      setTemplateCheckError(null);
      return;
    }
    let cancelled = false;
    setCheckingTemplates(true);
    api
      .checkWhatsappSignupTemplates(slug, { request_id: generateRequestIdV7() })
      .then((r) => {
        if (cancelled) return;
        setTemplateCheck({ ready: r.ready, rows: r.templates, liveRead: r.live_read });
        setTemplateCheckError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTemplateCheck(null);
        setTemplateCheckError(
          describeTemplateCheckFailure(e instanceof ApiError ? e.code : null),
        );
      })
      .finally(() => {
        if (!cancelled) setCheckingTemplates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, parkedComplete, templateNonce]);

  // 2 — the SDK (loaded once, only when the studio is configured).
  useEffect(() => {
    if (constants === null) return;
    if (window.FB !== undefined) {
      setSdkReady(true);
      return;
    }
    window.fbAsyncInit = () => {
      window.FB?.init({
        appId: constants.appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: constants.graphVersion,
      });
      setSdkReady(true);
    };
    if (document.getElementById(SDK_SCRIPT_ID) === null) {
      const script = document.createElement("script");
      script.id = SDK_SCRIPT_ID;
      script.src = SDK_SRC;
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      document.body.appendChild(script);
    }
  }, [constants]);

  // 4a — the popup's session-info messages.
  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      const parsed = parseEmbeddedSignupMessage(event.origin, event.data);
      if (parsed.kind === "finish") {
        setSession({
          wabaId: parsed.wabaId,
          phoneNumberId: parsed.phoneNumberId,
          event: parsed.event,
        });
        setCancelStep(null);
        setUnrecognised(null);
      } else if (parsed.kind === "cancel") {
        setCancelStep(parsed.currentStep ?? "(unknown step)");
        setUnrecognised(null);
        setPopupOpen(false);
      } else if (parsed.kind === "unrecognised") {
        setUnrecognised({ event: parsed.event, dataKeys: parsed.dataKeys });
        setPopupOpen(false);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // 4b/5 — auto-complete when both halves are in hand.
  useEffect(() => {
    if (slug === null || code === null || session === null) return;
    if (submittedRef.current) return;
    submittedRef.current = true;
    setCompleting(true);
    setCompleteError(null);
    api
      .completeWhatsappSignup(slug, {
        request_id: generateRequestIdV7(),
        code,
        waba_id: session.wabaId,
        ...(session.phoneNumberId !== null
          ? { phone_number_id: session.phoneNumberId }
          : {}),
      })
      .then((r) => {
        // The code is consumed either way — clear the run's halves so the
        // next launch starts clean (a stale latch here made a post-success
        // relaunch silently never POST).
        submittedRef.current = false;
        setCode(null);
        setSession(null);
        if (r.outcome === "phone_choice_required") {
          setPhoneChoice({
            wabaId: r.waba_id,
            rows: r.phone_numbers ?? [],
          });
        } else {
          setResult({
            waba_id: r.waba_id,
            phone_number_id: r.phone_number_id ?? "(unknown)",
            display_phone_number: r.display_phone_number ?? null,
          });
        }
        refreshConfig();
      })
      .catch((e: Error) => {
        setCompleteError(e.message);
        // Allow a retry with a FRESH popup run — the consumed code is dead.
        submittedRef.current = false;
        setCode(null);
        setSession(null);
      })
      .finally(() => {
        setCompleting(false);
      });
  }, [slug, code, session, refreshConfig]);

  const launch = (): void => {
    if (constants === null || window.FB === undefined) return;
    // A fresh run must never pair with a previous run's half-delivered
    // code or session — clear both plus the submit latch up front.
    submittedRef.current = false;
    setCode(null);
    setSession(null);
    setPopupOpen(true);
    setCancelStep(null);
    setCompleteError(null);
    window.FB.login(
      (response) => {
        setPopupOpen(false);
        const c = response.authResponse?.code;
        if (typeof c === "string" && c.length > 0) {
          setCode(c);
        }
      },
      {
        config_id: constants.configId,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          // Meta's coexistence page names this flag for the "connect your
          // existing WhatsApp Business app number" screen. This launch names
          // no version, so Meta serves it as v4, whose extras object is
          // "purposely empty": the flag is probably ignored (on 2026-10-27
          // the number was offered once its account sat in a portfolio the
          // popup lists; switch runbook §2a). Kept, since leaving it out is
          // untested.
          featureType: "whatsapp_business_app_onboarding",
          sessionInfoVersion: "3",
        },
      },
    );
  };

  const selectPhone = (phoneNumberId: string): void => {
    if (slug === null || phoneChoice === null) return;
    setSelecting(true);
    setCompleteError(null);
    api
      .selectWhatsappSignupPhone(slug, {
        request_id: generateRequestIdV7(),
        phone_number_id: phoneNumberId,
      })
      .then((r) => {
        setPhoneChoice(null);
        setResult({
          waba_id: r.waba_id,
          phone_number_id: r.phone_number_id,
          display_phone_number: r.display_phone_number,
        });
        refreshConfig();
      })
      .catch((e: Error) => {
        setCompleteError(e.message);
      })
      .finally(() => {
        setSelecting(false);
      });
  };

  const activate = (): void => {
    if (slug === null) return;
    setActivating(true);
    setActivateError(null);
    api
      .activateWhatsappSignup(slug, { request_id: generateRequestIdV7() })
      .then((r) => {
        setActivated({
          phone_number_id: r.phone_number_id,
          waba_id: r.waba_id,
          pendingCleared: r.pending_cleared,
          warning: r.warning ?? null,
        });
        setActivateArmed(false);
        refreshConfig();
      })
      .catch((e: unknown) => {
        setActivateError(
          describeActivateFailure(e instanceof ApiError ? e.code : null),
        );
        setActivateArmed(false);
        // The panels follow what is parked now; the error has its own.
        refreshConfig();
        recheckTemplates();
      })
      .finally(() => {
        setActivating(false);
        setResult(null);
      });
  };

  const pendingReady = parkedComplete;
  const templatesReady = templateCheck?.ready === true && !checkingTemplates;
  const pendingStranded =
    pending !== null &&
    pending.access_token &&
    (!pending.phone_number_id || !pending.waba_id) &&
    phoneChoice === null;

  return (
    <div>
      <h1 className="page-title">WhatsApp signup</h1>
      <p className="hint">
        Coexistence onboarding: connect the studio's WhatsApp Business App
        number to the Cloud API so the human keeps the app and the AI keeps
        the API — one number, both senders. The phone running the Business
        App must be in hand for the in-app confirmation step.
      </p>

      {loadError !== null ? (
        <div className="panel">
          <p className="error-text">Failed to load signup config: {loadError}</p>
        </div>
      ) : null}

      {configured === false ? (
        <div className="panel">
          <h2>Not configured</h2>
          <p className="hint">
            This studio has no <code>whatsapp_embedded_signup</code> config
            block (Meta app id + Embedded Signup config id), so the popup
            cannot launch. Seed it in the studio's L2 YAML or via a /config
            Save, then reload.
          </p>
        </div>
      ) : null}

      {activated !== null ? (
        <div className="panel">
          <h2>Production repointed ✓</h2>
          <p>
            The app is subscribed to WABA <code>{activated.waba_id}</code>, so
            the new number's messages reach the AI from now on, and live
            WhatsApp sends use phone number id{" "}
            <code>{activated.phone_number_id}</code>.{" "}
            {activated.pendingCleared
              ? "The pending credentials were promoted and cleared."
              : "The pending credentials were promoted."}
          </p>
          {activated.warning !== null ? (
            <p className="error-text">{activated.warning}</p>
          ) : null}
          <p className="hint">
            Next (runbook §7-9): run the live echo smoke walk, then the
            public wa.me sweep. Keep the Business App opened at least every
            13 days.
          </p>
          <button type="button" onClick={() => setActivated(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      {result !== null ? (
        <div className="panel">
          <h2>Number onboarded — parked as pending ✓</h2>
          <p>
            WhatsApp number{" "}
            <strong>{result.display_phone_number ?? "(unknown display)"}</strong>{" "}
            is connected to the Cloud API and its credentials are stored as
            PENDING. Production still sends from the current number, and the
            new number's messages do not reach the AI yet.
          </p>
          <ul>
            <li>
              WABA id: <code>{result.waba_id}</code>
            </li>
            <li>
              Phone number id: <code>{result.phone_number_id}</code>
            </li>
            <li>
              Business token parked. Webhooks are NOT subscribed yet —
              Activate subscribes them.
            </li>
          </ul>
          <p className="hint">
            Next steps (runbook §7): re-create on the new WABA every message
            template this studio sends, with the category you want, and wait
            until all of them are APPROVED. The Pending activation panel below
            lists them and keeps Activate locked until every one is: a
            template missing there would fail every send of it once production
            is repointed. Keep the Business App opened at least every 13 days.
          </p>
          <button type="button" onClick={() => setResult(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      {phoneChoice !== null ? (
        <div className="panel">
          <h2>Choose the phone number</h2>
          <p className="hint">
            WABA <code>{phoneChoice.wabaId}</code> lists more than one phone
            number and the popup did not say which one was onboarded. The
            token is already parked — pick the number this signup was for
            (no new popup run needed).
          </p>
          <ul>
            {phoneChoice.rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  disabled={selecting}
                  onClick={() => selectPhone(row.id)}
                >
                  {row.display_phone_number ?? "(unknown display)"} —{" "}
                  {row.id}
                </button>
              </li>
            ))}
          </ul>
          {selecting ? <p className="hint">Verifying the choice…</p> : null}
          {completeError !== null ? (
            <p className="error-text">Phone selection failed: {completeError}</p>
          ) : null}
        </div>
      ) : null}

      {pendingStranded && result === null ? (
        <div className="panel">
          <h2>Parked signup is incomplete</h2>
          <p className="hint">
            A pending token is parked, but its{" "}
            {pending?.phone_number_id ? "WABA id" : "phone number"} is
            missing (likely a reload mid-choice). Launch the signup again —
            the fresh run replaces the parked rows and re-offers the chooser.
          </p>
        </div>
      ) : null}

      {activateError !== null ? (
        <div className="panel">
          <h2>Activation did not finish</h2>
          <p className="error-text">{activateError}</p>
          <button type="button" onClick={() => setActivateError(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      {pendingReady ? (
        <div className="panel">
          <h2>Pending activation</h2>
          <p className="hint">
            A parked signup is ready. Activating subscribes the app to the
            new WABA, so the new number's messages start reaching the AI,
            and REPOINTS ALL production WhatsApp sends (AI replies, login
            codes, staff alerts) to the new number — both at once. It stays
            locked until every template this studio sends is APPROVED on the
            new WABA (runbook §6a and §7): a template missing there would fail
            every send of it from then on.
          </p>
          <h3>Templates on the new WABA</h3>
          {checkingTemplates ? (
            <p className="hint">Checking the new WABA's templates…</p>
          ) : null}
          {templateCheckError !== null ? (
            <p className="error-text">{templateCheckError}</p>
          ) : null}
          {templateCheck !== null ? (
            <>
              <p className={templateCheck.ready ? "hint" : "error-text"}>
                {templateCheck.ready
                  ? `All ${String(templateCheck.rows.length)} templates this studio can send are APPROVED on the new WABA.`
                  : `${String(templateCheck.rows.filter((r) => !r.ready).length)} of ${String(templateCheck.rows.length)} templates this studio can send are not APPROVED on the new WABA. Re-create them there (runbook §7); Activate stays locked until they are.`}
                {templateCheck.liveRead
                  ? ""
                  : " Today's account could not be read, so categories are not compared."}
              </p>
              <table className="data-table table-cards">
                <thead>
                  <tr>
                    <th>Template</th>
                    <th>Why it is needed</th>
                    <th style={{ width: "200px" }}>New WABA</th>
                    <th style={{ width: "140px" }}>Today</th>
                  </tr>
                </thead>
                <tbody>
                  {templateCheck.rows.map((row) => {
                    const state = templateRowState(row);
                    const note = templateCategoryNote(row);
                    return (
                      <tr key={`${row.name}:${row.language}`}>
                        <td className="table-card-title">
                          <code>{row.name}</code>{" "}
                          <span className="subtle">{row.language}</span>
                        </td>
                        <td data-label="Why it is needed">
                          {row.sources.map((src) => src.label).join("; ")}
                        </td>
                        <td data-label="New WABA">
                          <span className={`tag tag-${state.tone}`}>{state.text}</span>
                          {note !== null ? (
                            <>
                              {" "}
                              <span className="tag tag-warn">{note}</span>
                            </>
                          ) : null}
                        </td>
                        <td data-label="Today">
                          {row.live === null ? (
                            <span className="subtle">—</span>
                          ) : (
                            `${row.live.status} · ${row.live.category}`
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : null}
          <p>
            <button
              type="button"
              disabled={checkingTemplates}
              onClick={recheckTemplates}
            >
              {checkingTemplates ? "Checking…" : "Check again"}
            </button>
          </p>
          {activateArmed ? (
            <p className="error-text">
              Confirm: from the next message the AI answers the new number
              and every send leaves from it. This is the runbook §6 swap.
            </p>
          ) : null}
          <button
            type="button"
            disabled={activating || !templatesReady}
            title={
              templatesReady
                ? undefined
                : "Locked until every template above is APPROVED on the new WABA"
            }
            onClick={() => {
              if (activateArmed) {
                activate();
              } else {
                setActivateArmed(true);
              }
            }}
          >
            {activating
              ? "Activating…"
              : activateArmed
                ? "Confirm — repoint production now"
                : "Activate new number…"}
          </button>
          {activateArmed && !activating ? (
            <button type="button" onClick={() => setActivateArmed(false)}>
              Cancel
            </button>
          ) : null}
        </div>
      ) : null}

      {configured === true && result === null && phoneChoice === null ? (
        <div className="panel">
          <h2>Run the signup</h2>
          <p className="hint">
            The popup asks for the business's Facebook login, then offers
            "connect your existing WhatsApp Business app number". Decline
            chat-history sharing when the app asks (v1 decision — the echo
            lane only mirrors NEW messages).
          </p>
          <button
            type="button"
            disabled={!sdkReady || popupOpen || completing}
            onClick={launch}
          >
            {popupOpen
              ? "Popup open…"
              : completing
                ? "Completing…"
                : sdkReady
                  ? "Launch WhatsApp signup"
                  : "Loading Meta SDK…"}
          </button>
          {session !== null && code === null ? (
            <p className="hint">
              Signup finished (WABA {session.wabaId}) — waiting for the
              login callback's token code…
            </p>
          ) : null}
          {code !== null && session === null ? (
            <p className="hint">
              Token code received — waiting for the popup's session info…
            </p>
          ) : null}
          {completing ? (
            <p className="hint">
              Exchanging the code and verifying the number…
            </p>
          ) : null}
          {cancelStep !== null ? (
            <p className="hint">
              Signup was abandoned at step: <code>{cancelStep}</code>. Launch
              again to retry.
            </p>
          ) : null}
          {unrecognised !== null ? (
            <p className="error-text">
              The popup sent a WhatsApp-signup message this page could not
              read, so onboarding did not complete. Event:{" "}
              <code>{unrecognised.event}</code>; data keys:{" "}
              <code>
                {unrecognised.dataKeys.length > 0
                  ? unrecognised.dataKeys.join(", ")
                  : "(none)"}
              </code>
              . This usually means Meta changed the Embedded Signup payload
              shape — report these two values, they are what the parser needs
              to be taught.
            </p>
          ) : null}
          {completeError !== null ? (
            <p className="error-text">
              Completion failed: {completeError}. The popup code is single-use
              — launch the signup again to retry.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
